/*
 * Do not remove or alter the notices in this preamble.
 * This software code regards iO Custom Software.
 * Copyright © 2026 iO The Netherlands B.V. and/or its affiliates.
 * www.iodigital.com All rights reserved. License grant and user rights and obligations
 * according to applicable license agreement. Please contact legal@iodigital.com for
 * questions regarding license and user rights.
 */

import { consume } from '@lit/context';
import { css, html, LitElement } from 'lit';
import { customElement } from 'lit/decorators.js';

import { ConfiguratorContextType, configuratorGlobal } from '@/context';
import { BREAKPOINT_MEDIUM_PX } from '@/settings';
import { baseStyles, skeletonStyles } from '@/styles';
import { classMap } from 'lit/directives/class-map.js';

@customElement('fg-print-option-step-skeleton-loader')
export class FgPrintOptionStepSkeletonLoader extends LitElement {
  @consume({ context: configuratorGlobal.context, subscribe: true })
  configuratorContext!: ConfiguratorContextType;

  isNotCollapsible() {
    return this.configuratorContext.printOptionCollapsible !== 1;
  }

  render() {
    const collapsibleModifier = {
      'is-not-collapsible': this.isNotCollapsible(),
    };

    return html`
      <div class="skeleton-container ${classMap(collapsibleModifier)}">
        ${this.isNotCollapsible()
          ? html`
              <div class="skeleton-header">
                <div class="skeleton-title-large"></div>
              </div>
              <div class="skeleton-text skeleton-text-spaced"></div>
              <div class="skeleton-text skeleton-text-spaced"></div>
              <div class="skeleton-content">
                ${Array.from({ length: 3 }).map(
                  () => html`
                    <div class="skeleton-option-card">
                      <div class="skeleton-option-image"></div>
                      <div class="skeleton-option-text-wrapper">
                        <div class="skeleton-option-title"></div>
                        <div class="skeleton-option-description"></div>
                      </div>
                    </div>
                  `,
                )}
              </div>
              <div class="skeleton-text skeleton-text-spaced"></div>
            `
          : html`
              <div class="skeleton-header">
                <div class="skeleton-title"></div>
                <div class="skeleton-icon"></div>
              </div>
            `}
      </div>
    `;
  }

  static styles = [
    baseStyles,
    skeletonStyles,
    css`
      :host {
        --skeleton-content-bg: var(--skeleton-gray-light);
        --skeleton-card-bg: var(--fg-gray--200, var(--fg-fallback-gray--200));
        --skeleton-gap-collapsible: 10px;
      }

      .skeleton-container.is-not-collapsible .skeleton-header {
        flex-direction: column;
        justify-content: flex-start;
        align-items: flex-start;
        gap: var(--skeleton-gap-collapsible);
        margin-bottom: var(--skeleton-gap-default);
      }

      .skeleton-text-spaced {
        margin-top: 1rem;
      }

      .skeleton-option-text-wrapper {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
      }

      .skeleton-content {
        display: none;
      }

      .skeleton-container.is-not-collapsible .skeleton-content {
        display: grid;
        grid-template-columns: 1fr;
        gap: var(--skeleton-gap-default);
        margin-top: var(--skeleton-gap-default);
        margin-bottom: var(--skeleton-gap-default);
      }

      .skeleton-option-card {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 0.75rem;
        padding: 1rem;
        border-radius: var(--skeleton-border-radius-lg);
        background-color: var(--skeleton-card-bg);
      }

      .skeleton-option-image {
        width: 100%;
        height: 64px;
        width: 64px;
        border-radius: var(--skeleton-border-radius);
        background: var(--skeleton-gray-dark);
      }

      .skeleton-option-title,
      .skeleton-option-description,
      .skeleton-option-hsp {
        background: var(--skeleton-gray-dark);
        border-radius: var(--skeleton-border-radius);
        height: 1rem;
        width: 8rem;
        text-align: center;
      }

      @media screen and (min-width: ${BREAKPOINT_MEDIUM_PX}px) {
        .skeleton-container.is-not-collapsible .skeleton-content {
          grid-template-columns: repeat(2, 1fr);
        }
      }
    `,
  ];
}
